/**
 * ANTHROPIC-STYLE MINIMALIST DASHBOARD REDESIGN
 *
 * Overview:
 * We will refresh the dashboard's design to mirror Anthropic's minimalist and professional aesthetic 
 * with a touch of playfulness. This means keeping the interface clean and uncluttered, while introducing 
 * gentle cartoonish elements (like friendly shapes and animations) to make it inviting. All existing brand 
 * colors will be retained, applied thoughtfully via DaisyUI's theme system (so the palette stays on-brand).
 * Below we break down the key refinements – from smoother corners to micro-interactions – and how to implement 
 * each using React, DaisyUI v5, and Tailwind CSS v4. The result will be a UI that is modern, accessible, and 
 * consistent, with subtle personality.
 *
 * 1. Smooth Rounded Corners for a Softer Look
 *    - Use consistent, slightly larger border-radius (e.g., rounded-lg or rounded-xl) on cards, buttons, modals, etc.
 *    - Avatars/buttons might use rounded-full. 
 *    - Avoid mixing sharp corners with rounded ones for a harmonious look.
 *
 * 2. Exaggerated Padding and Ample Spacing
 *    - Create an airy layout by increasing padding inside components (p-6 or p-8) and spacing between them (space-y-6, etc.).
 *    - Prevent cramped UI, improve readability and click/tap accessibility.
 *
 * 3. Modern, Highly Legible Typography with Subtle Contrast
 *    - Use a clean, modern sans-serif font (e.g., Inter, Roboto).
 *    - Ensure high contrast for text (e.g., text-base-content vs text-base-content/90 for secondary text).
 *    - Keep font sizes at least 1rem or larger for body text.
 *
 * 4. Playful Micro-Interactions (Hover & Button Effects)
 *    - Incorporate small animations on hover (slight scale-up, color shift, icon wiggle).
 *    - Use Tailwind transitions (e.g., transition-transform duration-200 ease-out hover:scale-102).
 *    - Keep them subtle and consistent; avoid large or jarring effects.
 *
 * 5. Soft Drop Shadows for Depth (No Overwhelming Glow)
 *    - Use light, diffused shadows (shadow, shadow-md) for cards, modals, dropdowns.
 *    - Avoid high opacity or large offsets. 
 *    - Keep it minimal so the design remains clean and professional.
 *
 * 6. Slightly Thicker UI Borders for Structure
 *    - Increase border width from 1px to ~2px for a more polished, outlined appearance (border-2).
 *    - Combine with rounded corners for a friendly, pill-like shape on badges or pills.
 *    - Ensure border color gently contrasts with the background (e.g., border-base-300 or neutral-200).
 *
 * 7. Fluid Animated Transitions for UI Changes
 *    - For toggling menus, switching pages, or filtering content, use smooth transitions/fade/slide.
 *    - Keep animations around 200–300ms for responsiveness and minimal delay.
 *    - For mounting/unmounting, consider React Transition Group or Framer Motion if desired.
 *
 * 8. Grid-Based Alignment and Consistency
 *    - Enforce a strong grid system (grid-cols, consistent gaps, alignment) to ensure everything lines up neatly.
 *    - Use an 8-point or 4-point spacing system for uniform paddings/margins.
 *    - Maintain balanced layouts on all screen sizes.
 *
 * 9. Filter Feature Adjacent to Logo for Easy Access
 *    - (In the Navbar file) Position the filter/search control near the logo to increase discoverability.
 *    - Make the filter visually clear with a distinct input or a funnel icon, with consistent hover/focus states.
 *
 * 10. Improved UI Feedback with Color Shifts & Indicators
 *    - Provide immediate feedback on hover (e.g., color shift, scale, or pressed state).
 *    - Use semantic colors (success, warning, error, info) for statuses; show toasts or in-context alerts.
 *    - Display loading spinners or success/error icons promptly to keep users informed.
 *
 * By applying these refinements, the dashboard remains clean, accessible, and professional, with a subtle, 
 * friendly touch.
 */

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import DashNavbar from "./DashNavbar";
import Preloader from "./Preloader";
import FeedbackButton from "./FeedbackButton";
import ClicoSearch from "./ClicoSearch";
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import TermsAcceptance from "../TermsAcceptance";

// Assets
import babyHappy from "../../assets/gardelogo.png";
import pensionGuideLogo from "../../assets/modifpensionlogo.png";
import divorceGuideLogo from "../../assets/divorcelogo.png";
import sepLegLogo from "../../assets/seplogo.png";
import videoLogo from "../../assets/video.png";
import legalAidLogo from "../../assets/handshake.png";
import lawyerLogo from "../../assets/law.png";
import calculatorLogo from "../../assets/calculator.png";
import clicoPeekover from "../../assets/clicopeakover.png"; // Import the robot peek image

// Language flags component
const LanguageFlags = ({ availableLanguages }) => {
  const { i18n } = useTranslation();

  const languages = {
    fr: { name: 'Français', flag: '🇫🇷' },
    en: { name: 'English', flag: '🇬🇧' }
  };

  const handleLanguageChange = (langCode) => {
    if (availableLanguages.includes(langCode)) {
      i18n.changeLanguage(langCode);
    }
  };

  return (
    <div className="inline-flex items-center gap-1 bg-white bg-opacity-80 backdrop-blur-sm px-2 py-1 rounded-full border border-gray-200 shadow-sm">
      {Object.entries(languages).map(([code, lang]) => {
        const isAvailable = availableLanguages.includes(code);
        const isActive = i18n.language === code;
        return (
          <div
            key={code}
            className={`relative group ${isAvailable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            onClick={() => isAvailable && handleLanguageChange(code)}
          >
            <div className={`
              w-6 h-6 flex items-center justify-center rounded-full transition-all duration-200
              ${isAvailable ? 'hover:scale-110 hover:shadow-sm' : 'opacity-40 grayscale'}
              ${isActive ? 'ring-2 ring-blue-500 scale-110' : ''}
            `}>
              <span className="text-sm">{lang.flag}</span>
            </div>
            <div className={`
              absolute -bottom-10 left-1/2 transform -translate-x-1/2 z-10
              text-xs px-2 py-1 rounded-md shadow-md whitespace-nowrap
              opacity-0 group-hover:opacity-100 transition-opacity duration-200
              ${isAvailable ? 'bg-blue-700 text-white' : 'bg-gray-600 text-white'}
            `}>
              {isAvailable ? lang.name : `${lang.name} (Soon)`}
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-inherit"></div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Language badge component for individual cards
const LanguageBadge = ({ language }) => {
  const { i18n } = useTranslation();
  const languages = {
    fr: { name: 'Français', flag: '🇫🇷' },
    en: { name: 'English', flag: '🇬🇧' }
  };
  
  // Define which languages are available for each guide type
  const availableLanguages = {
    // Main guides
    divorce: ['fr', 'en'],
    separation: ['fr', 'en'],
    commonLaw: ['fr', 'en'],
    pension: ['fr', 'en'],
    
    // Calculators
    spousePension: ['fr', 'en'],
    childPension: ['fr', 'en'],
    
    // Orientation
    legalAid: ['fr', 'en'],
    lawyers: ['fr', 'en'],
    
    // Videos - only English available
    videos: ['en'],
    
    // Default to French and English for other items
    default: ['fr', 'en']
  };
  
  // Special case for videos section where only English is available
  if (language === 'en') {
    return (
      <div className="absolute top-2 right-2 bg-white bg-opacity-80 backdrop-blur-sm px-1.5 py-0.5 rounded-full border border-gray-200 shadow-sm">
        <span className="text-xs">{languages.en.flag}</span>
      </div>
    );
  }
  
  // Get available languages for this specific item
  const itemLanguages = availableLanguages[language] || availableLanguages.default;
  
  // Use the current language if it's available in the supported languages for this item
  // Default to French if current language is not supported
  const displayLanguage = itemLanguages.includes(i18n.language) ? i18n.language : 'fr';
  
  return (
    <div 
      className="absolute top-2 right-2 bg-white bg-opacity-80 backdrop-blur-sm px-1.5 py-0.5 rounded-full border border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-all duration-200"
      onClick={(e) => {
        e.stopPropagation();
        // Toggle between available languages
        const nextLanguage = displayLanguage === 'fr' ? 'en' : 'fr';
        if (itemLanguages.includes(nextLanguage)) {
          i18n.changeLanguage(nextLanguage);
        }
      }}
      title={`${languages[displayLanguage].name} - Click to change language`}
    >
      <span className="text-xs">{languages[displayLanguage].flag}</span>
    </div>
  );
};

// Content filter button component
const FilterButton = ({ active, label, icon, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
      ${active 
        ? 'bg-blue-600 text-white shadow-md transform scale-105' 
        : 'bg-white text-blue-600 border border-blue-200 hover:bg-blue-50'}`}
  >
    <span className="w-5 h-5 flex items-center justify-center">{icon}</span>
    <span>{label}</span>
  </button>
);

// Tab button component for main navigation
const TabButton = ({ active, label, icon, onClick, isNew }) => (
  <button
    onClick={onClick}
    data-tab={label.toLowerCase().replace(/\s+/g, '-')}
    className={`relative flex items-center px-6 py-3 font-medium transition-all duration-200
      ${active 
        ? 'bg-blue-600 text-white' 
        : 'bg-slate-100 text-blue-700'}`}
    style={{
      borderTopLeftRadius: active ? '8px' : '0px',
      borderTopRightRadius: active ? '8px' : '0px',
    }}
  >
    <span className="flex items-center gap-2">
      <span className="w-5 h-5 flex items-center justify-center">{icon}</span>
      <span className="font-medium">{label}</span>
      {isNew && (
        <span className="ml-1 bg-red-500 text-white text-xs px-1 py-0.5 rounded-full font-bold text-[10px]">
          NEW
        </span>
      )}
    </span>
  </button>
);

// Get icon for content type
const getContentIcon = (contentType) => {
  switch(contentType) {
    case 'guides': return '📚';
    case 'timelines': return '⏱️';
    case 'checklists': return '✅';
    case 'calculators': return '🧮';
    case 'orientation': return '🧭';
    case 'videos': return '🎥';
    default: return '📄';
  }
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [welcomeOpen, setWelcomeOpen] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCaseType, setActiveCaseType] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const location = useLocation();
  const { t, i18n } = useTranslation('dashboard');
  const [activeSection, setActiveSection] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Content types for filtering
  const contentTypes = [
    { id: 'all', label: t('dashboard.filters.all', 'All Content'), icon: '🔍' },
    { id: 'guides', label: t('dashboard.filters.guides', 'Guides'), icon: '📚' },
    { id: 'timelines', label: t('dashboard.filters.timelines', 'Timelines'), icon: '⏱️' },
    { id: 'checklists', label: t('dashboard.filters.checklists', 'Checklists'), icon: '✅' },
    { id: 'calculators', label: t('dashboard.filters.calculators', 'Calculators'), icon: '🧮' },
    { id: 'orientation', label: t('dashboard.filters.orientation', 'Orientation'), icon: '🧭' },
  ];
  
  // Types de dossiers
  const caseTypes = [
    { id: 'divorce', label: t('dashboard.caseTypes.divorce', 'Divorce'), icon: '💔' },
    { id: 'garde', label: t('dashboard.caseTypes.custody', 'Garde d\'enfant'), icon: '👶' },
    { id: 'sepleg', label: t('dashboard.caseTypes.legalSep', 'Séparation légale'), icon: '📝' },
    { id: 'pension', label: t('dashboard.caseTypes.pension', 'Pension alimentaire'), icon: '💰' }
  ];

  // Get logo for a specific content item
  const getLogoForContent = (sectionType, itemKey) => {
    switch(sectionType) {
      case 'guides':
      case 'timelines':
      case 'checklists':
        return itemKey === 'divorce' ? divorceGuideLogo : 
               itemKey === 'sepleg' || itemKey === 'separation' ? sepLegLogo :
               itemKey === 'garde' || itemKey === 'commonLaw' ? babyHappy :
               itemKey === 'pension' ? pensionGuideLogo :
               pensionGuideLogo;
      case 'calculators':
        // Pour les calculateurs, utiliser des logos spécifiques selon le type
        return itemKey === 'divorce' ? divorceGuideLogo :
               itemKey === 'separation' ? sepLegLogo :
               itemKey === 'commonLaw' ? babyHappy :
               itemKey === 'pension' || itemKey === 'spousePension' || itemKey === 'childPension' ? pensionGuideLogo :
               calculatorLogo;
      case 'orientation':
        return itemKey === 'legalAid' ? legalAidLogo : 
               itemKey === 'lawyers' ? lawyerLogo : 
               sepLegLogo;
      case 'videos':
        return videoLogo;
      default:
        return pensionGuideLogo;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Gestion du scroll initial
    if (location.state?.scrollTo) {
      setTimeout(() => {
        scrollToSection(location.state.scrollTo);
        // If scrolling to a specific section, activate that filter
        const sectionFilter = contentTypes.find(type => type.id === location.state.scrollTo);
        if (sectionFilter) {
          setActiveFilter(sectionFilter.id);
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.state]);

  // Create a custom, faster smooth scroll utility
  const fastSmoothScroll = (targetPosition, duration = 300) => {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime = null;

    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const run = easeInOutQuad(timeElapsed, startPosition, distance, duration);
      window.scrollTo(0, run);
      if (timeElapsed < duration) requestAnimationFrame(animation);
    }

    // Easing function for smoother motion
    function easeInOutQuad(t, b, c, d) {
      t /= d / 2;
      if (t < 1) return c / 2 * t * t + b;
      t--;
      return -c / 2 * (t * (t - 2) - 1) + b;
    }

    requestAnimationFrame(animation);
  };

  // Enhanced scroll function that also updates active section
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const offset = 100; // Adjust this value based on your header
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      // Use the custom faster smooth scroll
      fastSmoothScroll(offsetPosition, 300); // 300ms duration for faster scrolling
      
      // Update active section
      // Only update activeSection if it's not a hidden one
      if (sectionId !== 'timelines' && sectionId !== 'videos') {
        setActiveSection(sectionId);
      }
    }
  };

  // Add scroll event listener to track active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for better UX
      
      // Check each section to see which one is currently in view
      const sections = ['guides', 'timelines', 'checklists', 'calculators', 'orientation'];
      
      for (const sectionId of sections) {
        const section = document.getElementById(sectionId);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Call once on mount to set initial active section
    handleScroll();
    
    // Clean up
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Should display section based on current filter and case type
  const shouldDisplaySection = (sectionId) => {
    // La section orientation est toujours visible
    if (sectionId === 'orientation') {
      return true;
    }

    // Timelines and Videos sections are always hidden (under construction)
    if (sectionId === 'timelines' || sectionId === 'videos') {
      return false; // Always hide these sections
    }
    
    // Si on est en mode "all content", on affiche tout sauf si un type de dossier est sélectionné
    if (activeFilter === 'all') {
      // Si un type de dossier est sélectionné, on affiche uniquement les sections qui peuvent avoir des éléments de ce type
      if (activeCaseType !== null) {
        // Ces sections peuvent contenir des éléments filtrés par type de dossier
        const filteredSections = ['guides', 'timelines', 'checklists', 'calculators', 'orientation'];
        return filteredSections.includes(sectionId);
      }
      return true;
    }
    
    // Sinon on affiche seulement la section filtrée
    return sectionId === activeFilter;
  };
  
  // Fonction pour déterminer si un élément spécifique doit être affiché
  const shouldDisplayItem = (sectionType, itemKey) => {
    // Si aucun type de dossier n'est sélectionné, on affiche tout (sauf exceptions ci-dessous)
    if (activeCaseType === null) {
      // Exception for calculators: only childPension is displayed
      if (sectionType === 'calculators') {
        return itemKey === 'childPension';
      }
      // Timelines and Videos items are never displayed
      if (sectionType === 'timelines' || sectionType === 'videos') {
        return false;
      }
      return true;
    }

    // La section orientation est toujours visible
    if (sectionType === 'orientation') {
      return true;
    }

    // Timelines and Videos items are never displayed
    if (sectionType === 'timelines' || sectionType === 'videos') {
      return false;
    }

    // Logique spécifique pour les calculateurs
    if (sectionType === 'calculators') {
      // Only childPension calculator is accessible for now
      if (itemKey === 'childPension') {
        return true;
      }
      // All other calculators are "under construction"
      return false; // Hide other calculators
    }

    // Guides, Checklists - Matching direct avec le type de dossier
    if (['guides', 'checklists'].includes(sectionType)) {
      return (
        (itemKey === 'divorce' && activeCaseType === 'divorce') ||
        (itemKey === 'separation' && activeCaseType === 'sepleg') ||
        (itemKey === 'commonLaw' && activeCaseType === 'garde') ||
        (itemKey === 'pension' && activeCaseType === 'pension')
      );
    }

    // Par défaut, on n'affiche pas
    return false;
  };

  // Reset des filtres
  const resetFilters = () => {
    setActiveFilter('all');
    setActiveCaseType(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <Preloader />;

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-sky-100 via-indigo-50 to-purple-100 text-gray-800 flex flex-col">
      <DashNavbar onSectionClick={scrollToSection} />

      {/* Container pour contenu principal - appliqué sur tous les éléments principaux pour cohérence visuelle */}
      <div className="container mx-auto max-w-6xl px-4">
        {/* Disclaimer and Construction Notice Section - Redesigned for better UI/UX */}
        {activeTab === 'dashboard' && (
          <div className="mt-6 mb-8">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl shadow-sm border border-amber-200 overflow-hidden">
              <div className="flex flex-col sm:flex-row items-stretch">
                {/* Left icon column - vertical orange gradient for visual impact */}
                <div className="bg-gradient-to-b from-orange-400 to-amber-500 sm:w-16 flex-shrink-0 flex items-center justify-center py-4 sm:py-0">
                  <ExclamationTriangleIcon className="h-8 w-8 text-white" />
                </div>
                
                {/* Content area */}
                <div className="p-4 sm:p-5 flex-grow">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex-grow">
                      <h3 className="text-lg font-bold text-amber-800 mb-1">Site en Construction</h3>
                      <p className="text-amber-700 text-sm leading-relaxed">
                        Merci de votre patience pendant que nous peaufinons les choses. Certaines fonctionnalités pourraient être limitées.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowTermsModal(true)}
                      className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg shadow-sm transition-all duration-150 flex items-center gap-2 hover:shadow hover:translate-y-[-1px]"
                    >
                      <InformationCircleIcon className="w-5 h-5" />
                      <span>Infos Importantes</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Tabs Navigation with Robot Peeking */}
        <div className="relative mt-2">
          {/* Tabs container */}
          <div className="flex relative overflow-x-auto pb-2 tab-container">
            
            

            {/* Robot positioning - adjusted for all screen sizes */}
            <div className="absolute z-50 pointer-events-none" style={{
              top: '-45px',
              right: '80px',
              width: '80px',
              height: '80px'
            }}>
              <img 
                src={clicoPeekover} 
                alt="Clico Robot Peeking Over Tabs" 
                className="w-full h-full object-contain hidden"
              />
            </div>
            
            {/* Tab Buttons */}
            <TabButton
              active={activeTab === 'dashboard'}
              label="Dashboard"
              icon="📊"
              onClick={() => setActiveTab('dashboard')}
            />
          </div>

          {/* Description tooltip under active tab */}
          <div className="bg-gray-800 text-white text-xs px-3 py-1 rounded-md mt-1 inline-block">
            {activeTab === 'dashboard' ? 
              "Navigation par catégorie d'informations juridiques" : 
              "" /* Removed ClicoSearch description */}
          </div>
        </div>
      </div>

      {activeTab === 'dashboard' ? (
        <main className="container mx-auto max-w-6xl px-4 py-6 mt-2 space-y-8">
          {/* Global smooth scroll style */}
          <style>
            {`
              html {
                scroll-behavior: smooth;
              }
              section {
                scroll-margin-top: 6rem;
              }
              /* Consistent card hover effect */
              .card-hover {
                transition: all 0.25s ease;
              }
              .card-hover:hover {
                transform: translateY(-4px);
                box-shadow: 0 10px 25px rgba(59, 130, 246, 0.1);
              }
              /* Trimmed text with ellipsis */
              .text-trim {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
              }
              /* Section spacing */
              .section-grid {
                gap: 1.25rem;
              }
              /* Card padding */
              .card-padding {
                padding: 1.5rem;
              }
              /* Filter transitions */
              .filter-transition {
                transition: all 0.3s ease-in-out;
              }
              .filter-transition:enter {
                opacity: 0;
                transform: translateY(10px);
              }
              .filter-transition:enter-active {
                opacity: 1;
                transform: translateY(0);
              }
              .filter-transition:exit {
                opacity: 1;
                transform: translateY(0);
              }
              .filter-transition:exit-active {
                opacity: 0;
                transform: translateY(10px);
              }
              /* Expand/collapse transition */
              .filter-content {
                max-height: 0;
                overflow: hidden;
                transition: max-height 0.35s ease-in-out;
              }
              .filter-content.expanded {
                max-height: 1000px;
              }
              /* Focus styles for better accessibility */
              button:focus, a:focus {
                outline: 2px solid rgba(59, 130, 246, 0.5);
                outline-offset: 2px;
              }
              /* Improved button focus states */
              .btn:focus {
                box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(59, 130, 246, 0.5);
              }
            `}
          </style>

          {/* WELCOME SECTION - Only shown when no filters are active */}
          {welcomeOpen && activeFilter === 'all' && activeCaseType === null && (
            <section
              id="welcomeSection"
              className="relative bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 shadow-md overflow-hidden"
            >
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-blue-700 hover:text-blue-800 
                         bg-white hover:bg-blue-50 rounded-full w-8 h-8 
                         flex items-center justify-center transition-colors
                         border border-blue-200 shadow-sm"
                onClick={() => setWelcomeOpen(false)}
                aria-label={t('dashboard.welcome.close')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Content Section */}
                  <div className="flex-1 space-y-6">
                    {/* Header */}
                    <div className="space-y-2">
                      <h2 className="text-xl font-bold text-blue-700 flex items-center">
                        <span className="bg-blue-100 p-2 rounded-full mr-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </span>
                        {t('dashboard.welcome.title')}
                      </h2>
                      <p className="text-blue-600 text-sm">
                        {t('dashboard.welcome.subtitle')}
                      </p>
                    </div>

                    {/* How to use section */}
                    <div className="bg-white rounded-lg p-6 border border-blue-100 shadow-sm">
                      <h3 className="text-sm font-semibold text-blue-700 mb-4">{t('dashboard.welcome.howTo.title')}</h3>
                      <ol className="space-y-4">
                        <li className="flex items-start">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 mr-3">1</span>
                          <p className="text-sm text-gray-600">{t('dashboard.welcome.howTo.step1')}</p>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 mr-3">2</span>
                          <p className="text-sm text-gray-600">{t('dashboard.welcome.howTo.step2')}</p>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 mr-3">3</span>
                          <p className="text-sm text-gray-600">{t('dashboard.welcome.howTo.step3')}</p>
                        </li>
                      </ol>
                    </div>

                    {/* Features */}
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        {
                          icon: "📚",
                          title: t('dashboard.welcome.features.guides.title'),
                          desc: t('dashboard.welcome.features.guides.desc')
                        },
                        {
                          icon: "⏱️",
                          title: t('dashboard.welcome.features.timelines.title'),
                          desc: t('dashboard.welcome.features.timelines.desc')
                        },
                        {
                          icon: "✅",
                          title: t('dashboard.welcome.features.checklists.title'),
                          desc: t('dashboard.welcome.features.checklists.desc')
                        },
                        {
                          icon: "🧮",
                          title: t('dashboard.welcome.features.calculators.title'),
                          desc: t('dashboard.welcome.features.calculators.desc')
                        }
                      ].map((feature, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 border border-blue-100 shadow-sm">
                          <div className="text-2xl mb-2">{feature.icon}</div>
                          <h3 className="text-sm font-semibold text-blue-700">{feature.title}</h3>
                          <p className="text-xs text-gray-600">{feature.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Unified Filter Navigation - Now expandable */}
          <div className="bg-white rounded-xl border border-blue-100 shadow-md sticky top-20 z-10 overflow-hidden transition-shadow duration-300 hover:shadow-lg">
            {/* Filter Header with expand/collapse button */}
            <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setIsFilterExpanded(!isFilterExpanded)}>
              <h3 className="text-blue-700 font-semibold text-base flex items-center">
                <span className="mr-2 bg-blue-50 p-1.5 rounded-full text-blue-600">🔍</span>
                {t('dashboard.filterPanel', 'Filtrer le contenu')}
              </h3>
              <button className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-full transition-colors">
                {isFilterExpanded ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Filter content - expandable */}
            <div className={`filter-content ${isFilterExpanded ? 'expanded' : ''}`}>
              <div className="p-5 pt-0 space-y-4">
                {/* Reset button */}
                {(activeFilter !== 'all' || activeCaseType !== null) && (
                  <div className="flex justify-end">
                    <button
                      onClick={resetFilters}
                      className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1.5 rounded-full transition-colors duration-200 flex items-center font-medium"
                    >
                      <span className="mr-1">⟲</span>
                      {t('dashboard.resetFilters', 'Réinitialiser')}
                    </button>
                  </div>
                )}
                
                {/* Separator line */}
                <div className="border-t border-blue-100 -mx-5"></div>
                
                {/* Case Type Filters - Primary focus */}
                <div>
                  <h4 className="text-blue-700 font-medium text-sm mb-3 flex items-center">
                    <span className="bg-blue-100 p-1 rounded-full mr-2 text-xs">📁</span>
                    {t('dashboard.caseTypeTitle', 'Type de dossier:')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <FilterButton
                      active={activeCaseType === null}
                      label={t('dashboard.caseTypes.all', 'Tous les dossiers')}
                      icon="📁"
                      onClick={() => {
                        setActiveCaseType(null);
                        // Si un filtre de section est actif, le conserver
                        if (activeFilter !== 'all') {
                          scrollToSection(activeFilter);
                        }
                      }}
                    />
                    {caseTypes.map(type => (
                      <FilterButton
                        key={type.id}
                        active={activeCaseType === type.id}
                        label={type.label}
                        icon={type.icon}
                        onClick={() => {
                          setActiveCaseType(type.id);
                          // Si on sélectionne un type de dossier, on garde le filtre de section actif
                          // ou on revient à 'all' si c'était sur une section qui ne contient pas ce type de dossier
                          if (activeFilter !== 'all' && !['guides', 'timelines', 'checklists', 'calculators', 'orientation'].includes(activeFilter)) {
                            setActiveFilter('all');
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Separator line */}
                <div className="border-t border-blue-100 -mx-5"></div>
                
                {/* Content Type Filters - Secondary focus */}
                <div>
                  <h4 className="text-blue-700 font-medium text-sm mb-3 flex items-center">
                    <span className="bg-blue-100 p-1 rounded-full mr-2 text-xs">🔎</span>
                    {t('dashboard.filterTitle', 'Type de contenu:')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <FilterButton
                      key="all"
                      active={activeFilter === 'all'}
                      label={t('dashboard.filters.all', 'Tout le contenu')}
                      icon="🔍"
                      onClick={() => {
                        setActiveFilter('all');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                    {/* Groupe les filtres de contenu par catégorie */}
                    <div className="flex flex-wrap gap-2">
                      {contentTypes
                        .filter(type => type.id !== 'all')
                        .map(type => (
                          <FilterButton
                            key={type.id}
                            active={activeFilter === type.id}
                            label={type.label}
                            icon={type.icon}
                            onClick={() => {
                              setActiveFilter(type.id);
                              scrollToSection(type.id);
                            }}
                          />
                        ))}
                    </div>
                  </div>
                </div>
                
                {/* Active filters summary */}
                {(activeFilter !== 'all' || activeCaseType !== null) && (
                  <div className="mt-3 bg-blue-50 p-3 rounded-lg text-sm text-blue-700 flex items-center">
                    <span className="mr-2 bg-blue-100 p-1 rounded-full">🔍</span>
                    <span>
                      {activeCaseType !== null && activeFilter !== 'all' 
                        ? t('dashboard.activeFiltersBoth', 'Affichage: {{contentType}} pour {{caseType}}', {
                            contentType: contentTypes.find(t => t.id === activeFilter)?.label,
                            caseType: caseTypes.find(t => t.id === activeCaseType)?.label
                          })
                        : activeCaseType !== null
                          ? t('dashboard.activeFiltersCaseOnly', 'Affichage: Contenus pour {{caseType}}', {
                              caseType: caseTypes.find(t => t.id === activeCaseType)?.label
                            })
                          : t('dashboard.activeFiltersContentOnly', 'Affichage: {{contentType}}', {
                              contentType: contentTypes.find(t => t.id === activeFilter)?.label
                            })
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* GUIDES SECTION - Slate theme */}
          {shouldDisplaySection('guides') && (
            <section id="guides" className="bg-slate-50 rounded-lg p-5 border border-slate-200 shadow-sm">
              <div className="text-center relative">
                <div className="absolute top-0 right-0 mt-1 mr-1">
                  <LanguageFlags availableLanguages={['fr', 'en']} />
                </div>
                <h2 className="text-lg font-bold text-blue-700 mb-2">
                  <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
                  {t('dashboard.sections.guides.title')}
                  <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
                  {/* Updated Beta indicator for section */}
                  <span className="ml-2 inline-flex items-center bg-blue-600 text-white text-xs px-2.5 py-0.5 rounded-full font-medium shadow-sm">
                    <span className="mr-0.5 font-bold">β</span> BETA
                  </span>
                </h2>
                <p className="mt-4 text-sm text-gray-600 max-w-xl mx-auto mb-8">
                  {t('dashboard.sections.guides.subtitle')}
                </p>
              </div>
              
              <div className="grid section-grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {Object.entries(t('dashboard.sections.guides.items', { returnObjects: true }))
                  .filter(([key]) => shouldDisplayItem('guides', key))
                  .map(([key, item]) => (
                  <div key={key} className="bg-white border border-blue-100 rounded-lg card-padding shadow-sm card-hover flex flex-col items-center text-center relative">
                    <LanguageBadge language={key} />
                    <div className="bg-blue-50 p-2 rounded-full mb-2">
                      <img
                        src={getLogoForContent('guides', key)}
                        alt={`Icône ${item.title}`}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-blue-700 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-600 flex-grow mb-3 text-trim">{item.desc}</p>
                    <Link
                      to={
                        key === 'divorce' ? '/guides/divorce' : 
                        key === 'separation' ? '/guides/separation' : 
                        key === 'pension' ? '/guides/pension' : 
                        key === 'commonLaw' ? '/guides/garde' : 
                        `/guides/${key}`
                      }
                      className="btn btn-primary btn-xs w-full"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      {t('dashboard.sections.guides.button')}
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* TIMELINE SECTION - Marked as under construction */}
          {shouldDisplaySection('timelines') && (
            <section id="timelines" className="bg-amber-50 rounded-lg p-5 border border-amber-100 shadow-sm text-center">
                <div className="absolute top-0 right-0 mt-1 mr-1">
                  <LanguageFlags availableLanguages={['fr', 'en']} />
                </div>
                <h2 className="text-lg font-bold text-blue-700 mb-2">
                  <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
                  {t('dashboard.sections.timelines.title')}
                  <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
                  <span className="ml-2 inline-flex items-center bg-blue-600 text-white text-xs px-2.5 py-0.5 rounded-full font-medium shadow-sm">
                    <span className="mr-0.5 font-bold">β</span> BETA
                  </span>
                </h2>
                <p className="mt-4 text-sm text-gray-600 max-w-xl mx-auto mb-8">
                  {t('dashboard.sections.timelines.subtitle')}
                </p>
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md" role="alert">
                  <p className="font-bold">{t('dashboard.underConstruction.title', 'En Construction')}</p>
                  <p>{t('dashboard.underConstruction.timelines', 'Les frises interactives sont en cours de développement et seront bientôt disponibles.')}</p>
                </div>
            </section>
          )}

          {/* CHECKLISTS SECTION */}
          {shouldDisplaySection('checklists') && (
            <section id="checklists" className="bg-blue-100 rounded-lg p-5 border border-blue-200 shadow-sm">
              <div className="text-center relative">
                <div className="absolute top-0 right-0 mt-1 mr-1">
                  <LanguageFlags availableLanguages={['fr', 'en']} />
                </div>
                <h2 className="text-lg font-bold text-blue-700 mb-2">
                  <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
                  {t('dashboard.sections.checklists.title')}
                  <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
                  {/* Updated Beta indicator for section */}
                  <span className="ml-2 inline-flex items-center bg-blue-600 text-white text-xs px-2.5 py-0.5 rounded-full font-medium shadow-sm">
                    <span className="mr-0.5 font-bold">β</span> BETA
                  </span>
                </h2>
                <p className="mt-4 text-sm text-gray-600 max-w-xl mx-auto mb-8">
                  {t('dashboard.sections.checklists.subtitle')}
                </p>
              </div>
              
              <div className="grid section-grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {Object.entries(t('dashboard.sections.checklists.items', { returnObjects: true }))
                  .filter(([key]) => shouldDisplayItem('checklists', key))
                  .map(([key, item]) => (
                  <div key={key} className="bg-white border border-blue-200 rounded-lg card-padding shadow-sm card-hover flex flex-col items-center text-center relative">
                    <LanguageBadge language={key} />
                    <div className="bg-blue-50 p-2 rounded-full mb-2">
                      <img
                        src={getLogoForContent('checklists', key)}
                        alt={`Icône ${item.title}`}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-blue-700 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-600 flex-grow mb-3 text-trim">{item.desc}</p>
                    <Link
                      to={
                        key === 'divorce' ? '/checklists/divorce' : 
                        key === 'separation' ? '/checklists/separation' : 
                        key === 'pension' ? '/checklists/pension' : 
                        key === 'commonLaw' ? '/checklists/garde' : 
                        `/checklists/${key}`
                      }
                      className="btn btn-primary btn-xs w-full"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      {t('dashboard.sections.checklists.button')}
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CALCULATORS SECTION */}
          {shouldDisplaySection('calculators') && (
            <section id="calculators" className="bg-slate-100 rounded-lg p-5 border border-slate-200 shadow-sm">
              <div className="text-center relative">
                <div className="absolute top-0 right-0 mt-1 mr-1">
                  <LanguageFlags availableLanguages={['fr', 'en']} />
                </div>
                <h2 className="text-lg font-bold text-blue-700 mb-2">
                  <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
                  {t('dashboard.sections.calculators.title')}
                  <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
                  {/* Updated Beta indicator for section */}
                  <span className="ml-2 inline-flex items-center bg-blue-600 text-white text-xs px-2.5 py-0.5 rounded-full font-medium shadow-sm">
                    <span className="mr-0.5 font-bold">β</span> BETA
                  </span>
                </h2>
                <p className="mt-4 text-sm text-gray-600 max-w-xl mx-auto mb-8">
                  {t('dashboard.sections.calculators.subtitle')}
                </p>
              </div>
              
              <div className="grid section-grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {Object.entries(t('dashboard.sections.calculators.items', { returnObjects: true }))
                  .filter(([key]) => !['spousePension', 'childPension'].includes(key) && shouldDisplayItem('calculators', key))
                  .map(([key, item]) => (
                  <div key={key} className="bg-white border border-slate-200 rounded-lg card-padding shadow-sm card-hover flex flex-col items-center text-center relative">
                    <LanguageBadge language={key} />
                    <div className="bg-slate-100 p-2 rounded-full mb-2">
                      <img
                        src={getLogoForContent('calculators', key)}
                        alt={`Icône ${item.title}`}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-blue-700 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-600 flex-grow mb-3 text-trim">{item.desc}</p>
                    <Link
                      to={
                        key === 'divorce' ? '/calculators/divorce' : 
                        key === 'separation' ? '/calculators/separation' : 
                        key === 'pension' ? '/calculators/pension' : 
                        key === 'commonLaw' ? '/calculators/garde' : 
                        `/calculators/${key}`
                      }
                      className="btn btn-primary btn-xs w-full"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      {t('dashboard.sections.calculators.button')}
                    </Link>
                  </div>
                ))}
                {/* Display "Coming Soon" for other main calculators if all filters are off or calculators filter is on */}
                {(activeFilter === 'all' || activeFilter === 'calculators') && activeCaseType === null && Object.entries(t('dashboard.sections.calculators.items', { returnObjects: true }))
                  .filter(([key]) => !['spousePension', 'childPension'].includes(key) && !shouldDisplayItem('calculators', key))
                  .map(([key, item]) => (
                    <div key={key} className="bg-white border border-slate-200 rounded-lg card-padding shadow-sm flex flex-col items-center text-center relative opacity-70">
                      <LanguageBadge language={key} />
                      <div className="bg-slate-100 p-2 rounded-full mb-2">
                        <img
                          src={getLogoForContent('calculators', key)}
                          alt={`Icône ${item.title}`}
                          className="w-10 h-10 object-contain"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-blue-700 mb-1">{item.title}</h3>
                      <p className="text-xs text-gray-600 flex-grow mb-3 text-trim">{item.desc}</p>
                      <div className="btn btn-disabled btn-xs w-full">
                        {t('dashboard.sections.calculators.comingSoon', 'Bientôt disponible')}
                      </div>
                       {/* Hidden link for direct access */}
                       <Link to={key === 'divorce' ? '/calculators/divorce' : key === 'separation' ? '/calculators/separation' : key === 'pension' ? '/calculators/pension' : key === 'commonLaw' ? '/calculators/garde' : `/calculators/${key}`} className="hidden"></Link>
                    </div>
                  ))}
              </div>

              {/* Pension calculators */}
              <div className="mt-8">
                <h3 className="text-md font-semibold text-blue-700 mb-4 text-center">
                  <span className="inline-block h-1 bg-warning w-4 mr-2 rounded-full align-middle"></span>
                  {t('dashboard.sections.calculators.specificTitle', 'Specific Calculators')}
                  <span className="inline-block h-1 bg-warning w-4 ml-2 rounded-full align-middle"></span>
                  {/* Updated Beta indicator for subsection */}
                  <span className="ml-2 inline-flex items-center bg-blue-600 text-white text-xs px-2.5 py-0.5 rounded-full font-medium shadow-sm">
                    <span className="mr-0.5 font-bold">β</span>
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {Object.entries(t('dashboard.sections.calculators.items', { returnObjects: true }))
                    .filter(([key]) => {
                      // Afficher toujours les deux calculateurs si aucun filtre n'est actif
                      if (activeCaseType === null) {
                        return ['spousePension', 'childPension'].includes(key);
                      }

                      // Pour divorce et separation légale, afficher les deux
                      if (['divorce', 'sepleg'].includes(activeCaseType)) {
                        return ['spousePension', 'childPension'].includes(key);
                      }
                      
                      // Pour garde et pension, afficher seulement pension pour enfants
                      if (['garde', 'pension'].includes(activeCaseType)) {
                        return key === 'childPension';
                      }
                      
                      return false;
                    })
                    .map(([key, item]) => (
                    <div key={key} className="bg-blue-50 border border-amber-100 rounded-lg card-padding shadow-sm card-hover flex flex-col items-center text-center relative">
                      <LanguageBadge language={key} />
                      <div className="bg-amber-100 p-2 rounded-full mb-2">
                        <img
                          src={calculatorLogo}
                          alt={`Icône ${item.title}`}
                          className="w-10 h-10 object-contain"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-blue-700 mb-1">{item.title}</h3>
                      <p className="text-xs text-gray-600 flex-grow mb-3 text-trim">{item.desc}</p>
                      {key === 'spousePension' ? (
                        <>
                          <div className="btn btn-disabled btn-xs w-full mb-1">
                             {t('dashboard.sections.calculators.comingSoon', 'Bientôt disponible')}
                          </div>
                          {/* Hidden link for direct access */}
                          <Link to={`/calculators/divorce-pension`} className="hidden"></Link>
                        </>
                      ) : ( // key === 'childPension'
                        <a
                          href="https://cpa.justice.gouv.qc.ca/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-warning btn-xs w-full"
                        >
                          {t('dashboard.sections.calculators.calculate')}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ORIENTATION SECTION - Always visible */}
          {shouldDisplaySection('orientation') && (
            <section id="orientation" className="bg-amber-50 rounded-lg p-5 border border-amber-100 shadow-sm">
              <div className="text-center relative">
                <div className="absolute top-0 right-0 mt-1 mr-1">
                  <LanguageFlags availableLanguages={['fr', 'en']} />
                </div>
                <h2 className="text-lg font-bold text-blue-700 mb-2 flex items-center justify-center">
                  <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
                  {t('dashboard.sections.orientation.title')}
                  <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
                  {/* Updated Beta indicator for section */}
                  <span className="ml-2 inline-flex items-center bg-blue-600 text-white text-xs px-2.5 py-0.5 rounded-full font-medium shadow-sm">
                    <span className="mr-0.5 font-bold">β</span> BETA
                  </span>
                </h2>
                <p className="mt-4 text-sm text-gray-600 max-w-xl mx-auto mb-8">
                  {t('dashboard.sections.orientation.subtitle')}
                </p>
              </div>
              
              <div className="grid section-grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {Object.entries(t('dashboard.sections.orientation.items', { returnObjects: true })).map(([key, item]) => (
                  <div key={key} className="bg-white border border-amber-200 rounded-lg card-padding shadow-sm card-hover flex flex-col items-center text-center relative">
                    <LanguageBadge language={key} />
                    <div className="bg-amber-100 p-2 rounded-full mb-2">
                      <img
                        src={getLogoForContent('orientation', key)}
                        alt={`Icône ${item.title}`}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-blue-700 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-600 flex-grow mb-3 text-trim">{item.desc}</p>
                    <Link
                      to={key === 'legalAid' ? '/orientation/aide-juridique' : 
                          key === 'lawyers' ? '/orientation/cabinets' : 
                          '/orientation/ressources'}
                      className="btn btn-warning btn-xs w-full"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      {t('dashboard.sections.orientation.button')}
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* VIDEOS SECTION - Marked as under construction */}
          {shouldDisplaySection('videos') && (
            <section id="videos" className="bg-blue-50 rounded-lg p-5 border border-blue-100 shadow-sm text-center">
                <h2 className="text-lg font-bold text-blue-700 mb-2">
                  <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
                  {t('dashboard.sections.videos.title')}
                  <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
                </h2>
                <p className="mt-4 text-sm text-gray-600 max-w-xl mx-auto mb-8">
                  {t('dashboard.sections.videos.subtitle')}
                </p>
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md" role="alert">
                  <p className="font-bold">{t('dashboard.underConstruction.title', 'En Construction')}</p>
                  <p>{t('dashboard.underConstruction.videos', 'La section vidéos est en cours de développement et sera bientôt disponible.')}</p>
                </div>
            </section>
          )}

          {/* Message d'explication pour le cas où il n'y a pas de contenu pour le type de dossier sélectionné */}
          {activeCaseType !== null && 
           activeFilter !== 'orientation' &&
           !Object.entries(t('dashboard.sections.guides.items', { returnObjects: true }))
              .some(([key]) => shouldDisplayItem('guides', key)) &&
           !Object.entries(t('dashboard.sections.checklists.items', { returnObjects: true }))
              .some(([key]) => shouldDisplayItem('checklists', key)) &&
           !Object.entries(t('dashboard.sections.calculators.items', { returnObjects: true }))
              .some(([key]) => shouldDisplayItem('calculators', key)) && (
            <div className="bg-white border border-blue-200 p-5 rounded-lg shadow-sm mt-4">
              <div className="flex items-center justify-center flex-col text-center">
                <div className="text-5xl mb-4">⚠️</div>
                <h3 className="text-lg font-semibold text-blue-700 mb-2">
                  {t('dashboard.noCaseTypeContent', 'Aucun contenu spécifique disponible')}
                </h3>
                <p className="text-gray-600 max-w-md mx-auto mb-4">
                  {t('dashboard.caseTypeContentMessage', 'Nous travaillons actuellement sur du contenu spécifique pour ce type de dossier. En attendant, vous pouvez consulter la section orientation.')}
                </p>
                <button
                  onClick={() => {
                    scrollToSection('orientation');
                  }}
                  className="btn btn-primary btn-sm"
                >
                  {t('dashboard.goToOrientation', 'Consulter la section orientation')}
                </button>
              </div>
            </div>
          )}

          {/* No content message when filtered but no matching content */}
          {activeFilter !== 'all' && activeFilter !== 'orientation' && 
           !shouldDisplaySection(activeFilter) && 
           activeFilter !== 'timelines' && // Don't show this message if timelines or videos are selected, as they have their own "under construction" message
           activeFilter !== 'videos' && (
            <div className="flex flex-col items-center justify-center p-12 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-lg font-semibold text-blue-700 mb-2">
                {t('dashboard.noContentForFilter', 'No content available for this filter')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('dashboard.tryDifferentFilter', 'Try selecting a different filter or view all content')}
              </p>
              <button
                onClick={() => setActiveFilter('all')}
                className="btn btn-primary btn-sm"
              >
                {t('dashboard.viewAllContent', 'View All Content')}
              </button>
            </div>
          )}
        </main>
      ) : null /* Removed ClicoSearch component rendering */ }

      <FeedbackButton />

      {/* Render TermsAcceptance modal conditionally */}
      {showTermsModal && <TermsAcceptance onClose={() => setShowTermsModal(false)} isInformational={true} />}
    </div>
  );
};

export default Dashboard;
